import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';
import { logError } from 'utils/log';

import type { Action, Campaign, InfluencerTile, ThunkAction } from 'types';

const LOADING = 'bulkAddToShortlists/LOADING';
const INFLUENCERS_LOADED = 'bulkAddToShortlists/INFLUENCERS_LOADED';
const CAMPAIGNS_LOADED = 'bulkAddToShortlists/CAMPAIGNS_LOADED';
const RESET = 'bulkAddToShortlists/RESET';

const { LISTS, BRANDS, CAMPAIGNS, CREATOR_PROPOSAL } = API.END_POINTS;

const initialState = {
  areSubcampaignsLoading: false,
  areCampaignsLoading: false,
  futureSubcampaigns: [],
  campaigns: []
};

function setLoadingStatus(data): Action {
  return {
    type: LOADING,
    payload: {
      data
    }
  };
}

export function reset() {
  return {
    type: RESET,
    payload: {}
  };
}

function receiveInfluencers(influencers: Array<InfluencerTile>): Action {
  return {
    type: INFLUENCERS_LOADED,
    payload: {
      influencers
    }
  };
}

function receiveCampaigns(campaigns: Array<Campaign>): Action {
  return {
    type: CAMPAIGNS_LOADED,
    payload: {
      campaigns
    }
  };
}

export function fetchInfluencers(organizationId: number, listId: number): ThunkAction {
  return async (dispatch) => {
    const url = `${LISTS.getListInfluencers(organizationId, listId)}?page=1&items_per_page=1000&sort=followers.desc`;

    dispatch(setLoadingStatus({ areSubcampaignsLoading: true }));

    try {
      const response = await get(url);
      const influencers = response.data.data;
      dispatch(receiveInfluencers(influencers));
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(setLoadingStatus({ areSubcampaignsLoading: false }));
    }

    return null;
  };
}

export function fetchApprovedInfluencers(organizationId: number, listId: number): ThunkAction {
  return async (dispatch) => {
    const query = snakeCaseKeys({
      page: 1,
      itemsPerPage: 1000,
      sort: 'ig_followers.desc'
    });

    const url = `${CREATOR_PROPOSAL.creatorList(organizationId, listId, Querystring.objectToString(query))}`;

    dispatch(setLoadingStatus({ areSubcampaignsLoading: true }));

    try {
      const response = await get(url);
      const influencersRawData = response.data.data;
      const approvedInfluencers = influencersRawData
        .filter((entry) => entry.approvalStatus === 'approved')
        .map((entry) => entry.creatorProfile);
      dispatch(receiveInfluencers(approvedInfluencers));
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(setLoadingStatus({ areSubcampaignsLoading: false }));
    }

    return null;
  };
}

export function fetchCampaigns(filterBy: string): ThunkAction {
  return async (dispatch, getState) => {
    const currentOrganizationId = getState().organizations.currentOrganizationId;

    if (!currentOrganizationId) {
      return Promise.resolve();
    }

    const url = `${BRANDS.getBrandCampaignsV1(currentOrganizationId)}?items_per_page=1000&filter_by=${filterBy}`;

    dispatch(setLoadingStatus({ areCampaignsLoading: true }));

    try {
      const responses = await Promise.all([
        get(`${url}&campaign_state=preparation`),
        get(`${url}&campaign_state=active`)
      ]);

      dispatch(receiveCampaigns([...responses[0].data.data, ...responses[1].data.data]));
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(setLoadingStatus({ areCampaignsLoading: false }));
    }

    return null;
  };
}

export type CreateSubcampaignsBulkResponse = {
  campaignName: string,
  existingCreators: Array<string>
};

function bulkAddToCampaignShortlist(campaignId: number, influencerIds: Array<number>): Promise<{data: CreateSubcampaignsBulkResponse}> {
  const url = CAMPAIGNS.createSubcampaignsInBulk(campaignId);

  return post(url, {
    data: influencerIds.map(id => ({ creatorId: id }))
  })
    .catch((error) => {
      logError(error);
      throw error;
    });
}

export function performBulkAddToShortlists(campaignIds: Array<number>, influencerIds: Array<number>): ThunkAction {
  const loadingId = 'performBulkAddToShortlists';

  return async (dispatch) => {
    dispatch(showLoading(loadingId));

    try {
      const responses = await Promise.all(campaignIds.map(campaignId => bulkAddToCampaignShortlist(campaignId, influencerIds)));
      addUserAction({
        eventCategory: 'bulk-offer',
        eventAction: 'bulk-add-to-shortlists'
      });

      return responses.map(response => response.data);
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(hideLoading(loadingId));
    }
  };
}

export default createReducer<any, any>('bulkAddToShortlists', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { data }) => ({
    ...state,
    ...data
  }),
  [INFLUENCERS_LOADED]: (state, { influencers }) => ({
    ...state,
    futureSubcampaigns: influencers.map(i => ({ id: i.id, creatorProfile: i }))
  }),
  [CAMPAIGNS_LOADED]: (state, { campaigns }) => ({
    ...state,
    campaigns
  })
});
